import { useEffect, useState } from "react";
import { IoIosArrowUp } from "react-icons/io";

import Button from "../components/common/Button";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "../constants";
import { useTranslation } from "react-i18next";
import { getTerms } from "../api";
import Spinner from "../components/common/Spinner";
import Notification from "../components/Notification";
import { AxiosError } from "axios";

interface CheckboxState {
  [key: string]: boolean;
}

const PolicyAgreePage = () => {
  const [checkboxes, setCheckboxes] = useState<CheckboxState>({
    all: false,
    privacy: false,
    thirdParty: false,
    location: false,
    age: false,
    service: false,
    marketing: false
  });
  const [isDetail, setIsDetail] = useState(false);
  const [terms, setTerms] = useState<{ language: string; content: string }[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const termContent = terms.find((term) => term.language === i18n.language)?.content ?? terms[0]?.content ?? "";

  const toggleDetail = () => {
    setIsDetail((prev) => !prev);
  };

  const toggleAll = () => {
    const newState = !checkboxes.all;
    setCheckboxes({
      all: newState,
      privacy: newState,
      thirdParty: newState,
      location: newState,
      age: newState,
      service: newState,
      marketing: newState
    });
  };

  const toggleCheckbox = (key: string) => {
    setCheckboxes((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      newState.all =
        newState.privacy &&
        newState.thirdParty &&
        newState.location &&
        newState.age &&
        newState.service &&
        newState.marketing;
      return newState;
    });
  };

  const handleNavigation = () => {
    navigate(ROUTE_PATH.AUTH);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getTerms();
        setTerms(data);
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data.message);
          setIsError(true);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) return <Spinner />;
  if (isError) return <Notification message={errorMessage} />;

  const checkboxItems = [
    { key: "privacy", label: `${t("privacy_collect")} [${t("required")}]` },
    { key: "thirdParty", label: `${t("privacy_third_party")} [${t("required")}]` },
    { key: "location", label: `${t("location_service")} [${t("required")}]` },
    { key: "age", label: `${t("age_over_14")} [${t("required")}]` },
    { key: "service", label: `${t("smart_checkin_service")} [${t("required")}]` },
    { key: "marketing", label: `${t("marketing_consent")} [${t("optional")}]` }
  ];
  const isAllRequiredChecked = ["privacy", "thirdParty", "location", "age", "service"].every((key) => checkboxes[key]);

  return (
    <div className="p-4 rounded-lg shadow">
      <h1 className="text-xl font-bold mb-6">{t("policy_agree")}</h1>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-x-2">
          <input id="all" type="checkbox" checked={checkboxes.all} onChange={toggleAll} className="w-4 h-4" />
          <label htmlFor="all" className="font-semibold">
            {t("agree_all")}
          </label>
        </div>
        <Button size="xs" variant="outlined" onClick={toggleDetail}>
          {isDetail ? t("close") : t("detail")}
        </Button>
      </div>

      {isDetail && (
        <div className="flex flex-col justify-center items-center gap-y-3">
          <div className="text-sm text-gray-700 prose max-w-none" dangerouslySetInnerHTML={{ __html: termContent }} />
          <button type="button" onClick={toggleDetail}>
            <IoIosArrowUp size={22} />
          </button>
        </div>
      )}
      <hr className="my-4" />
      <div className="space-y-4 mb-8">
        {checkboxItems.map(({ key, label }) => (
          <div key={key} className="flex items-start gap-x-2">
            <input
              id={key}
              type="checkbox"
              checked={checkboxes[key]}
              onChange={() => toggleCheckbox(key)}
              className="w-4 h-4 mt-1 flex-shrink-0"
            />
            <label htmlFor={key} className="flex-grow mt-0.5 text-sm text-gray-900">
              {label}
            </label>
          </div>
        ))}
      </div>

      <Button onClick={handleNavigation} disabled={!isAllRequiredChecked}>
        {t("confirm")}
      </Button>
    </div>
  );
};

export default PolicyAgreePage;
